/**
 * US Retirement Planner API Service
 * Handles API calls for US retirement planning with Monte Carlo simulation
 */

const API_BASE = '/backend/us-retirement';

// ==================== REQUEST TYPES ====================

export interface USIncomeStage {
  stage: number;
  starting_age: number;
  ending_age: number;
  starting_income: number;
  ending_income: number;
  traditional_401k_contribution: number;
  roth_401k_contribution: number;
  traditional_ira_contribution: number;
  roth_ira_contribution: number;
  hsa_contribution: number;
}

export interface USUserProfile {
  // Demographics
  age: number;
  gender: 'Male' | 'Female';
  birth_year: number;
  state: string;
  filing_status: 'single' | 'married' | 'married_separate' | 'head_of_household';
  life_expectancy_scenario: 1 | 2 | 3 | 4; // 1=Conservative, 2=Moderate, 3=Aggressive, 4=Custom
  custom_life_expectancy?: number;

  // Spouse (if married)
  spouse_age?: number;
  spouse_gender?: 'Male' | 'Female';
  spouse_birth_year?: number;

  // Income
  current_income: number;
  income_stages?: USIncomeStage[];
  retirement_age: number;
  inflation_rate: number;

  // Spouse Income
  spouse_current_income?: number;
  spouse_income_stages?: USIncomeStage[];
  spouse_retirement_age?: number;

  // Account Balances
  traditional_401k_balance: number;
  traditional_ira_balance: number;
  roth_401k_balance: number;
  roth_ira_balance: number;
  roth_ira_contribution_basis: number;
  hsa_balance: number;
  taxable_brokerage_balance: number;
  taxable_brokerage_cost_basis: number;

  // Spouse Accounts
  spouse_traditional_401k_balance?: number;
  spouse_traditional_ira_balance?: number;
  spouse_roth_401k_balance?: number;
  spouse_roth_ira_balance?: number;
  spouse_hsa_balance?: number;

  // Investment Assumptions
  stock_return_rate: number;
  bond_return_rate: number;
  stock_allocation: number;
  volatility: number;

  // Spending
  annual_spending: number;
  spending_scenario: 'exact' | 'fluctuating' | 'increased';
  healthcare_annual_cost: number;

  // Social Security
  ss_claiming_strategy: 'early' | 'fra' | 'delayed' | 'optimize' | 'custom';
  ss_claiming_age?: number;
  estimated_ss_pia?: number;
  ss_earnings_history_years?: number;

  // Spouse Social Security
  spouse_ss_claiming_strategy?: 'early' | 'fra' | 'delayed' | 'optimize' | 'custom';
  spouse_ss_claiming_age?: number;
  spouse_estimated_ss_pia?: number;

  // Pension
  pension_amount?: number;
  pension_start_age?: number;
  pension_cola: number;
  spouse_pension_amount?: number;
  spouse_pension_start_age?: number;

  // Strategies
  withdrawal_strategy: 'traditional_first' | 'roth_first' | 'proportional' | 'tax_efficient';
  roth_conversion_strategy: 'none' | 'fill_bracket' | 'avoid_irmaa' | 'aggressive' | 'custom';
  roth_conversion_amount?: number;
  avoid_irmaa: boolean;
}

export interface USSimulationRequest {
  user_profile: USUserProfile;
  num_scenarios: number;
  year: number;
}

// ==================== RESPONSE TYPES ====================

export interface USYearlyCashFlow {
  year: number;
  age: number;
  employment_income: number;
  social_security_income: number;
  pension_income: number;
  traditional_withdrawal: number;
  roth_withdrawal: number;
  roth_conversion: number;
  total_tax: number;
  annual_spending: number;
  total_portfolio_value: number;
  traditional_balance: number;
  roth_balance: number;
  hsa_balance: number;
  taxable_balance: number;
}

export interface USSummaryStats {
  mean_final_balance: number;
  median_final_balance: number;
  percentile_10_balance: number;
  percentile_90_balance: number;
  success_rate: number;
  scenarios_successful: number;
  total_scenarios: number;
  mean_taxes_paid: number;
  mean_roth_conversions: number;
}

export interface USRiskAnalysis {
  worst_case_balance: number;
  best_case_balance: number;
  standard_deviation: number;
  scenarios_depleted: number;
  earliest_depletion_year: number | null;
  probability_of_ruin: number;
}

export interface USSampleScenario {
  life_expectancy: number;
  ss_claiming_age: number;
  total_final_balance: number;
  total_taxes_paid: number;
  success_rate: number;
  yearly_cashflows: USYearlyCashFlow[];
}

export interface USSimulationResponse {
  summary_stats: USSummaryStats;
  recommendations: string[];
  risk_analysis: USRiskAnalysis;
  optimal_ss_claiming_age: number;
  optimal_roth_conversion_strategy: string;
  num_scenarios: number;
  sample_scenario: USSampleScenario;
}

export interface USQuickEstimateRequest {
  age: number;
  retirement_age: number;
  current_income: number;
  annual_savings: number;
  current_savings: number;
  expected_return: number;
  inflation_rate: number;
  life_expectancy?: number;
}

export interface USQuickEstimateResponse {
  years_to_retirement: number;
  projected_savings_at_retirement: number;
  safe_annual_withdrawal: number;
  estimated_social_security: number;
  total_annual_income: number;
  replacement_ratio: number;
  notes: string[];
}

export interface WithdrawalStrategy {
  name: string;
  description: string;
  annual_tax: number;
  net_withdrawal: number;
  pros: string[];
  cons: string[];
}

export interface WithdrawalStrategyResponse {
  strategies: WithdrawalStrategy[];
  recommendation: string;
}

// ==================== API FUNCTIONS ====================

export const USRetirementAPI = {
  /**
   * Run full Monte Carlo retirement simulation
   */
  async runSimulation(request: USSimulationRequest): Promise<USSimulationResponse> {
    const response = await fetch(`${API_BASE}/simulate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Simulation failed');
    }
    return data.data;
  },

  /**
   * Get quick retirement estimate without full Monte Carlo
   */
  async getQuickEstimate(request: USQuickEstimateRequest): Promise<USQuickEstimateResponse> {
    const response = await fetch(`${API_BASE}/quick-estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Quick estimate failed');
    }
    return data.data;
  },

  /**
   * Compare withdrawal strategies
   */
  async compareWithdrawalStrategies(
    traditional_balance: number,
    roth_balance: number,
    taxable_balance: number,
    annual_withdrawal: number,
    tax_rate: number
  ): Promise<WithdrawalStrategyResponse> {
    const response = await fetch(`${API_BASE}/withdrawal-strategy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        traditional_balance,
        roth_balance,
        taxable_balance,
        annual_withdrawal,
        tax_rate,
      }),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Strategy comparison failed');
    }
    return data.data;
  },

  /**
   * Get contribution limits for retirement accounts
   */
  async getContributionLimits(year: number = 2024): Promise<any> {
    const response = await fetch(`${API_BASE}/contribution-limits?year=${year}`);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get contribution limits');
    }
    return data.data;
  },

  /**
   * Calculate RMD for an account
   */
  async calculateRMD(account_balance: number, age: number, year: number = 2024): Promise<any> {
    const response = await fetch(`${API_BASE}/rmd`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account_balance, age, year }),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'RMD calculation failed');
    }
    return data.data;
  },

  /**
   * Calculate Roth conversion tax impact
   */
  async calculateRothConversionTax(
    conversion_amount: number,
    taxable_income: number,
    filing_status: string,
    state: string,
    year: number = 2024
  ): Promise<any> {
    const response = await fetch(`${API_BASE}/roth-conversion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conversion_amount,
        taxable_income,
        filing_status,
        state,
        year,
      }),
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Roth conversion calculation failed');
    }
    return data.data;
  },

  /**
   * Get age milestones
   */
  async getAgeMilestones(age: number): Promise<any> {
    const response = await fetch(`${API_BASE}/milestones?age=${age}`);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get milestones');
    }
    return data.data;
  },

  /**
   * Create default user profile
   */
  createDefaultProfile(): USUserProfile {
    const currentYear = new Date().getFullYear();
    return {
      age: 35,
      gender: 'Male',
      birth_year: currentYear - 35,
      state: 'California',
      filing_status: 'single',
      life_expectancy_scenario: 2,

      current_income: 100000,
      retirement_age: 65,
      inflation_rate: 2.5,

      traditional_401k_balance: 100000,
      traditional_ira_balance: 0,
      roth_401k_balance: 0,
      roth_ira_balance: 50000,
      roth_ira_contribution_basis: 30000,
      hsa_balance: 10000,
      taxable_brokerage_balance: 50000,
      taxable_brokerage_cost_basis: 40000,

      stock_return_rate: 7.0,
      bond_return_rate: 4.0,
      stock_allocation: 70,
      volatility: 12,

      annual_spending: 60000,
      spending_scenario: 'exact',
      healthcare_annual_cost: 6000,

      ss_claiming_strategy: 'optimize',
      ss_earnings_history_years: 15,

      pension_cola: 0,

      withdrawal_strategy: 'tax_efficient',
      roth_conversion_strategy: 'fill_bracket',
      avoid_irmaa: true,
    };
  },
};

export default USRetirementAPI;
